import type { ApiFailure } from "@lafayette/api-client";
import type { StaffImportMappingIssue, StaffImportParseError } from "@lafayette/schemas";

import { describeStaffFailure, STAFF_WORDS } from "../failure-words";

// How the importer tells a staff member what went wrong. The message is chosen by the kind of failure
// (the HTTP status, a parse error list, a mapping issue list). It never copies the server's message,
// a file name, a header or a cell: the server's text is not shown at all. The 422 lists carry codes
// and positions only; `labels.ts` turns them into sentences.

export type ImportFailureKind =
  | "conflict"
  | "parse"
  | "mapping"
  | "invalid"
  | "too_large"
  | "forbidden"
  | "not_found"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "upload"
  | "response"
  | "unknown";

export type ImportFailure = Readonly<{
  kind: ImportFailureKind;
  message: string;
  /** The same command may run again with the same idempotency key (no answer, a 503, a 429). */
  retryable: boolean;
  requestId: string | null;
  parseErrors: readonly StaffImportParseError[];
  mappingIssues: readonly StaffImportMappingIssue[];
}>;

/** A 400 or a 422: why the file or the mapping was refused, when the body says. */
function refusedWords(api: ApiFailure): { kind: ImportFailureKind; message: string } {
  const refusal = api.detail.importRefusal;
  if (refusal !== null && refusal.parseErrors.length > 0) {
    return { kind: "parse", message: "The server could not read this file." };
  }
  if (refusal !== null && refusal.mappingIssues.length > 0) {
    return { kind: "mapping", message: "The server did not accept this mapping." };
  }
  return {
    kind: "invalid",
    message: "The server did not accept this request. Check it and try again.",
  };
}

export function describeImportError(error: unknown): ImportFailure {
  const { api, kind, message, retryable, requestId } = describeStaffFailure<ImportFailureKind>(
    error,
    {
      conflict: "This import was changed by someone else, or it can no longer be changed.",
      invalid: refusedWords,
      forbidden: "Your role cannot do this.",
      unavailable: {
        message: "The import service is not available right now. Try again in a moment.",
      },
      on: {
        bad_request: refusedWords,
        too_large: { kind: "too_large", message: "The file is too large. The limit is 10 MB." },
        unauthenticated:
          "Your session has ended. Sign in again. Your choices are still on this page.",
        not_found: {
          kind: "not_found",
          message: "This import was not found. It may not exist, or you may not have access.",
        },
        rate_limited: STAFF_WORDS.rateLimited,
        // The storage refused the file, or never answered.
        upload: (failure) => ({
          kind: "upload",
          message:
            failure.status === 0
              ? "The file could not be sent to storage. Check the connection and try again."
              : "Storage did not accept the file. Choose the file again.",
        }),
        // A server failure that the import service does not retry on its own (500, 502, 504).
        server: { kind: "unknown", message: STAFF_WORDS.otherStatus, retryable: false },
      },
    },
  );
  const refusal = api.detail.importRefusal;
  return {
    kind,
    message,
    retryable,
    requestId,
    parseErrors: kind === "parse" ? (refusal?.parseErrors ?? []) : [],
    mappingIssues: kind === "mapping" ? (refusal?.mappingIssues ?? []) : [],
  };
}
